import React, { useState } from 'react';
import { ACTIVITY_LABEL, ActivityType } from '../../lib/activityTypes';
import { DataSource, DATA_SOURCES, DATA_SOURCE_LABEL } from '../../lib/dataSources';
import { useActivityCatalog, ActivityDefinition } from '../../lib/activityCatalog';
import { api, handleApiError } from '../../lib/api';

const emptyForm = { id: '', name: '', type: 'DRILLING' as ActivityType, sources: [] as DataSource[] };

const ActivityCatalogPage: React.FC = () => {
  const { catalog, refresh } = useActivityCatalog();
  const [form, setForm] = useState(emptyForm);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await api.activityDefinitions.update(form.id, { name: form.name, type: form.type, sources: form.sources });
      } else {
        await api.activityDefinitions.create({ name: form.name, type: form.type, sources: form.sources });
      }
      setForm(emptyForm);
      setIsEditing(false);
      await refresh();
    } catch (e: any) {
      setError(handleApiError(e));
    }
  };

  const handleEdit = (def: ActivityDefinition) => {
    setForm(def);
    setIsEditing(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this activity definition?')) return;
    try {
      await api.activityDefinitions.delete(id);
      await refresh();
    } catch (e: any) {
      setError(handleApiError(e));
    }
  };

  const toggleSource = (src: DataSource) => {
    setForm(prev => ({
      ...prev,
      sources: prev.sources.includes(src)
        ? prev.sources.filter(s => s !== src)
        : [...prev.sources, src]
    }));
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold mb-4">Activity Catalog</h2>
      {error && <p className="text-red-500">{error}</p>}
      <table className="w-full text-left border-collapse">
        <thead>
          <tr>
            <th className="p-2 border-b">Name</th>
            <th className="p-2 border-b">Type</th>
            <th className="p-2 border-b">Sources</th>
            <th className="p-2 border-b">Actions</th>
          </tr>
        </thead>
        <tbody>
          {(catalog || []).map(def => (
            <tr key={def.id} className="border-b">
              <td className="p-2">{def.name}</td>
              <td className="p-2">{ACTIVITY_LABEL[def.type]}</td>
              <td className="p-2">{def.sources.map(s => DATA_SOURCE_LABEL[s]).join(', ')}</td>
              <td className="p-2 space-x-2">
                <button className="text-blue-500" onClick={() => handleEdit(def)}>Edit</button>
                <button className="text-red-500" onClick={() => handleDelete(def.id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <form onSubmit={handleSubmit} className="space-y-4">
        <h3 className="text-lg font-semibold">{isEditing ? 'Edit' : 'Add'} Activity</h3>
        <input
          type="text"
          placeholder="Name"
          value={form.name}
          onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
          className="w-full p-2 border rounded bg-white text-black"
          required
        />
        <select
          value={form.type}
          onChange={e => setForm(prev => ({ ...prev, type: e.target.value as ActivityType }))}
          className="w-full p-2 border rounded bg-white text-black"
        >
          {(Object.keys(ACTIVITY_LABEL) as ActivityType[]).map(t => (
            <option key={t} value={t}>{ACTIVITY_LABEL[t]}</option>
          ))}
        </select>
        <div className="flex space-x-4">
          {DATA_SOURCES.map(s => (
            <label key={s} className="flex items-center space-x-1">
              <input
                type="checkbox"
                checked={form.sources.includes(s)}
                onChange={() => toggleSource(s)}
              />
              <span>{DATA_SOURCE_LABEL[s]}</span>
            </label>
          ))}
        </div>
        <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded">
          {isEditing ? 'Update' : 'Create'}
        </button>
      </form>
    </div>
  );
};

export default ActivityCatalogPage;
